import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pin, FileText, ExternalLink, Search } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import { formatLongDate } from "@/lib/dates";

const fmt = (d) => formatLongDate(d);

const CATEGORY_LABEL = {
  notice: "Public Notice",
  announcement: "Announcement",
  bid: "Bid / RFP",
  other: "Posting",
};

const PostingCard = ({ p, testKey }) => (
  <Card className={`border-border ${p.pinned ? "border-l-2 border-l-accent" : ""}`} data-testid={`posting-${p.id}`}>
    <CardContent className="p-7">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-accent mb-2">
        {p.pinned && <Pin size={12} />}
        <span>{CATEGORY_LABEL[p.category] || p.category}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{fmt(p.posting_date)}</span>
      </div>
      <h2 className="font-heading text-xl font-semibold">{p.title}</h2>
      {p.body && (
        <p className="mt-3 whitespace-pre-wrap text-foreground/85 leading-relaxed">{p.body}</p>
      )}
      {p.signed_by && (
        <p className="mt-4 text-sm italic text-muted-foreground">— {p.signed_by}</p>
      )}
      {p.file_id && (
        <a
          href={`${API_BASE}/files/${p.file_id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-md bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/90"
          data-testid={`${testKey}-file-link`}
        >
          <FileText size={14} /> Open posting document <ExternalLink size={12} className="opacity-70" />
        </a>
      )}
    </CardContent>
  </Card>
);

const Postings = () => {
  usePageTitle("Public Notices");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/postings").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = q
    ? items.filter((p) =>
        `${p.title} ${p.body || ""} ${p.signed_by || ""}`.toLowerCase().includes(q.toLowerCase()),
      )
    : items;

  const pinned = filtered.filter((p) => p.pinned);
  const others = filtered.filter((p) => !p.pinned);

  return (
    <div className="container-le section-pad" data-testid="postings-page">
      <div className="max-w-2xl mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-3">Township</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Public Notices</h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          Official notices, announcements, and postings from the Lake Eunice Township Board. Sorted by date — pinned items appear first.
        </p>
      </div>

      <div className="relative max-w-md mb-10">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search postings..."
          className="pl-9"
          data-testid="postings-search-input"
        />
      </div>

      {filtered.length === 0 && (
        <div className="border border-dashed border-border rounded-xl py-12 text-center text-muted-foreground bg-white">
          No postings yet.
        </div>
      )}

      {pinned.length > 0 && (
        <div className="space-y-5 mb-8">
          {pinned.map((p) => <PostingCard key={p.id} p={p} testKey={`posting-${p.id}`} />)}
        </div>
      )}

      <div className="space-y-5">
        {others.map((p) => <PostingCard key={p.id} p={p} testKey={`posting-${p.id}`} />)}
      </div>
    </div>
  );
};

export default Postings;
